import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()

  // Scroll efekti
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Aktif sayfa kontrolü
  const isActivePage = (path) => location.pathname === path

  // Mobil menü kapatma
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
    // Mobil menü kapatıldığında scroll pozisyonunu sıfırla
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    })
  }

  // Navigasyon link tıklama işleyicisi
  const handleNavigation = () => {
    // Mobil menüyü kapat
    setIsMobileMenuOpen(false)
    // Sayfanın en üstüne scroll yap
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    })
  }

  // Navigasyon menüsü
  const navigationItems = [
    { name: 'Hakkımızda', path: '/hakkimizda' },
    { name: 'Hizmetler', path: '/hizmetler' },
    { name: 'Referanslar', path: '/referanslar' },
    { name: 'İletişim', path: '/iletisim' }
  ]

  return (
    <>
      {/* Header */}
      <header 
        className={`
          fixed top-0 left-0 right-0 z-50 
          transition-all duration-500 ease-in-out
          ${isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100' 
            : 'bg-transparent'
          }
        `}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 md:h-18 lg:h-20">
            
            {/* Logo */}
            <Link 
           to="/" 
           onClick={() => {
             // Logo tıklandığında en üste scroll yap
             window.scrollTo({
               top: 0,
               left: 0,
               behavior: 'smooth'
             })
           }}
           className="flex items-center space-x-1 group"
         >
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="relative"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-xl flex items-center justify-center overflow-hidden">
                  <img 
                    src="/logo.png" 
                    alt="Flekspro Logo" 
                    className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-14 lg:h-14 object-cover"
                  />
                </div>

              </motion.div>
              
              <motion.span
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-lg sm:text-xl md:text-2xl lg:text-2xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent"
              >
                Flekspro
              </motion.span>
            </Link>

            {/* Desktop Navigation & CTA Buttons */}
            <div className="hidden sm:flex items-center space-x-4 md:space-x-6 lg:space-x-4">
              {/* Navigation Menu */}
              <nav className="flex items-center space-x-4 md:space-x-6 lg:space-x-4">
                {navigationItems.map((item, index) => (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
                  >
                                     <Link
                   to={item.path}
                   onClick={() => {
                     // Desktop navigation link tıklandığında en üste scroll yap
                     window.scrollTo({
                       top: 0,
                       left: 0,
                       behavior: 'smooth'
                     })
                   }}
                   className={`
                     relative text-base font-medium transition-all duration-300
                     ${isActivePage(item.path)
                       ? 'text-primary-600'
                       : 'text-gray-700 hover:text-primary-500'
                     }
                   `}
                 >
                      {item.name}
                      
                      {/* Aktif sayfa alt çizgisi */}
                      {isActivePage(item.path) && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"
                          initial={false}
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      )}
                      
                      {/* Hover alt çizgisi */}
                      <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-primary-400 to-primary-500 rounded-full transition-all duration-300 group-hover:w-full" />
                    </Link>
                  </motion.div>
                ))}
              </nav>

              {/* CTA Buttons */}
              <div className="flex items-center space-x-2 md:space-x-3 lg:space-x-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                               <Link
                 to="/hizmetal"
                 onClick={() => {
                   // CTA buton tıklandığında en üste scroll yap
                   window.scrollTo({
                     top: 0,
                     left: 0,
                     behavior: 'smooth'
                   })
                 }}
                 className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2.5 md:px-5 md:py-2.5 lg:px-8 lg:py-3 rounded-full text-xs sm:text-sm font-semibold text-primary-600 bg-white border-2 border-primary-600 hover:bg-primary-50 hover:border-primary-700 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg"
               >
                  Hizmet Al
                </Link>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                               <Link
                 to="/hizmetver"
                 onClick={() => {
                   // CTA buton tıklandığında en üste scroll yap
                   window.scrollTo({
                     top: 0,
                     left: 0,
                     behavior: 'smooth'
                   })
                 }}
                 className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2.5 md:px-5 md:py-2.5 lg:px-8 lg:py-3 rounded-full text-xs sm:text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-all duration-300 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl"
               >
                  Hizmet Ver
                </Link>
              </motion.div>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="sm:hidden">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsMobileMenuOpen(true)}
                className="p-2 rounded-lg text-primary-600 hover:bg-primary-50 transition-colors duration-200"
                aria-label="Menüyü aç"
              >
                <Bars3Icon className="w-6 h-6" />
              </motion.button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="sm:hidden fixed inset-0 z-50"
          >
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/20 backdrop-blur-sm"
              onClick={closeMobileMenu}
            />
            
            {/* Menu Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
                             className="absolute right-0 top-0 h-full w-72 sm:w-80 max-w-[90vw] sm:max-w-[85vw] bg-white shadow-2xl"
            >
              {/* Menu Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <Link to="/" onClick={closeMobileMenu} className="flex items-center space-x-3">
                                                                           <div className="w-12 h-12 rounded-xl flex items-center justify-center">
                      <img src="/logo.png" alt="Flekspro Logo" className="w-12 h-12 object-cover" />
                    </div>
                  <span className="text-xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">
                    Flekspro
                  </span>
                </Link>
                
                <button
                  onClick={closeMobileMenu}
                  className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                  aria-label="Menüyü kapat"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

                             {/* Menu Content */}
               <div className="p-6 space-y-4">
                 {/* Ana Menü Öğeleri */}
                 <div className="space-y-2">
                   <Link
                     to="/hakkimizda"
                     onClick={handleNavigation}
                     className={`
                       flex items-center px-4 py-3 rounded-xl text-base font-medium transition-all duration-200
                       ${isActivePage('/hakkimizda')
                         ? 'bg-primary-50 text-primary-600 border border-primary-100'
                         : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                       }
                     `}
                   >
                     <span className={`
                       w-2 h-2 rounded-full mr-3 transition-all duration-200
                       ${isActivePage('/hakkimizda')
                         ? 'bg-primary-500 scale-125'
                         : 'bg-gray-300'
                       }
                     `} />
                     Hakkımızda
                   </Link>
                   
                   <Link
                     to="/hizmetler"
                     onClick={handleNavigation}
                     className={`
                       flex items-center px-4 py-3 rounded-xl text-base font-medium transition-all duration-200
                       ${isActivePage('/hizmetler')
                         ? 'bg-primary-50 text-primary-600 border border-primary-100'
                         : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                       }
                     `}
                   >
                     <span className={`
                       w-2 h-2 rounded-full mr-3 transition-all duration-200
                       ${isActivePage('/hizmetler')
                         ? 'bg-primary-500 scale-125'
                         : 'bg-gray-300'
                       }
                     `} />
                     Hizmetler
                   </Link>
                   
                   <Link
                     to="/referanslar"
                     onClick={handleNavigation}
                     className={`
                       flex items-center px-4 py-3 rounded-xl text-base font-medium transition-all duration-200
                       ${isActivePage('/referanslar')
                         ? 'bg-primary-50 text-primary-600 border border-primary-100'
                         : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                       }
                     `}
                   >
                     <span className={`
                       w-2 h-2 rounded-full mr-3 transition-all duration-200
                       ${isActivePage('/referanslar')
                         ? 'bg-primary-500 scale-125'
                         : 'bg-gray-300'
                       }
                     `} />
                     Referanslar
                   </Link>
                   
                   <Link
                     to="/iletisim"
                     onClick={handleNavigation}
                     className={`
                       flex items-center px-4 py-3 rounded-xl text-base font-medium transition-all duration-200
                       ${isActivePage('/iletisim')
                         ? 'bg-primary-50 text-primary-600 border border-primary-100'
                         : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                       }
                     `}
                   >
                     <span className={`
                       w-2 h-2 rounded-full mr-3 transition-all duration-200
                       ${isActivePage('/iletisim')
                         ? 'bg-primary-500 scale-125'
                         : 'bg-gray-300'
                       }
                     `} />
                     İletişim
                   </Link>
                   
                   <Link
                     to="/hizmetal"
                     onClick={handleNavigation}
                     className={`
                       flex items-center px-4 py-3 rounded-xl text-base font-medium transition-all duration-200
                       ${isActivePage('/hizmetal')
                         ? 'bg-primary-50 text-primary-600 border border-primary-100'
                         : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                       }
                     `}
                   >
                     <span className={`
                       w-2 h-2 rounded-full mr-3 transition-all duration-200
                       ${isActivePage('/hizmetal')
                         ? 'bg-primary-500 scale-125'
                         : 'bg-gray-300'
                       }
                     `} />
                     Hizmet Al
                   </Link>
                   
                   <Link
                     to="/hizmetver"
                     onClick={handleNavigation}
                     className={`
                       flex items-center px-4 py-3 rounded-xl text-base font-medium transition-all duration-200
                       ${isActivePage('/hizmetver')
                         ? 'bg-primary-50 text-primary-600 border border-primary-100'
                         : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                       }
                     `}
                   >
                     <span className={`
                       w-2 h-2 rounded-full mr-3 transition-all duration-200
                       ${isActivePage('/hizmetver')
                         ? 'bg-primary-500 scale-125'
                         : 'bg-gray-300'
                       }
                     `} />
                     Hizmet Ver
                   </Link>
                 </div>

                 {/* İlk Çizgi */}
                 <div className="border-t border-gray-200 my-4"></div>

                 {/* Alt Menü Öğeleri */}
                 <div className="space-y-2">
                                        <Link
                       to="/onlineodeme"
                       onClick={handleNavigation}
                     className="flex items-center px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-all duration-200"
                   >
                     <span className="w-2 h-2 rounded-full mr-3 bg-gray-300" />
                     Online Ödeme
                   </Link>
                   
                                        <Link
                       to="/musteri"
                       onClick={handleNavigation}
                     className="flex items-center px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-all duration-200"
                   >
                     <span className="w-2 h-2 rounded-full mr-3 bg-gray-300" />
                     Müşteri
                   </Link>
                   
                                        <Link
                       to="/partner"
                       onClick={handleNavigation}
                     className="flex items-center px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-all duration-200"
                   >
                     <span className="w-2 h-2 rounded-full mr-3 bg-gray-300" />
                     Partner
                   </Link>
                 </div>

                 {/* İkinci Çizgi */}
                 <div className="border-t border-gray-200 my-4"></div>

                 {/* İletişim Butonları */}
                 <div className="space-y-3">
                   <a
                     href="tel:08505325989"
                     className="flex items-center justify-center w-full px-4 py-3 rounded-xl text-base font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-all duration-200 shadow-lg"
                   >
                     <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                     </svg>
                     Hemen Arayın
                   </a>
                   
                   <a
                     href="https://wa.me/905555555555"
                     target="_blank"
                     rel="noopener noreferrer"
                     className="flex items-center justify-center w-full px-4 py-3 rounded-xl text-base font-semibold bg-green-600 text-white hover:bg-green-700 transition-all duration-200 shadow-lg"
                   >
                     <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                       <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                     </svg>
                     WhatsApp'tan Yazın
                   </a>
                 </div>

                {/* Footer */}
                <div className="pt-6 border-t border-gray-200">
                  <div className="text-center space-y-1">
                                        <div className="flex items-center justify-center space-x-1">
                      <Link 
                        to="/" 
                        onClick={() => {
                          // Mobil menü logo tıklandığında en üste scroll yap
                          window.scrollTo({
                            top: 0,
                            left: 0,
                            behavior: 'smooth'
                          })
                        }}
                        className="flex items-center space-x-1 group"
                      >
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center">
                          <img src="/logo.png" alt="Flekspro Logo" className="w-8 h-8 object-cover" />
                        </div>
                        <span className="text-lg font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">
                          Flekspro
                        </span>
                      </Link>
                    </div>
                    <p className="text-xs text-gray-500">
                      © 2025 Flekspro A.Ş. Tüm hakları saklıdır.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default Header
